/**
 * 마스터 토글 설정 — 신규 점포 개설 절차 v2
 * 권위 문서: docs/console/2026-05-14_corporate-store-opening-procedure.md §6.2
 */

export type BusinessType = 'franchise' | 'corporate' | 'individual';
export type FoodServiceType = 'general' | 'rest' | 'bakery';
/** §3.6 법인 직영 과세 단위 — 양자택일 라디오 (branch-registration / none 제거) */
export type CorporateTaxMode = 'unit-tax' | 'separate';

export interface ToggleState {
  businessType: BusinessType;
  foodServiceType: FoodServiceType;
  isBasement: boolean;
  isGroundFloorDirect: boolean;
  isOverAreaThreshold: boolean;
  isNearSchool: boolean;
  isNewBuildOrRenovation: boolean;
  sellsAlcohol: boolean;
  usesLPG: boolean;
  hasLargeSignage: boolean;
  hiresForeignWorkers: boolean;
  sellsOnline: boolean;
  ownerHasLicense: boolean;
  ownerOnSite: boolean;
  /** 법인 직영 과세 단위 라디오 (default = 'separate') */
  corporateTaxMode: CorporateTaxMode;
  /** 상법상 지점 설치 등기 여부 — 과세 단위와 독립적으로 결합 가능 (default = false) */
  branchRegistration: boolean;
  /** 자사 온라인 주문 시스템 (배달앱 외 자사몰·앱 직접 결제) — PRE-PG 활성 조건 */
  hasOwnOnlineOrder: boolean;
  /** 방염 대상물품 (목재·커튼·카펫 등) 있나? — PRE-FIRE 활성 조건 */
  hasFlammableMaterials: boolean;
}

export const DEFAULT_TOGGLES: ToggleState = {
  businessType: 'franchise',
  foodServiceType: 'general',
  isBasement: false,
  isGroundFloorDirect: true,
  isOverAreaThreshold: false,
  isNearSchool: false,
  isNewBuildOrRenovation: false,
  sellsAlcohol: true,
  usesLPG: false,
  hasLargeSignage: false,
  hiresForeignWorkers: false,
  sellsOnline: true,
  ownerHasLicense: false,
  ownerOnSite: true,
  corporateTaxMode: 'separate',
  branchRegistration: false,
  hasOwnOnlineOrder: false,
  hasFlammableMaterials: false,
};

export type ToggleCategory =
  | 'business-type'
  | 'food-service-type'
  | 'space'
  | 'property'
  | 'operations'
  | 'staffing'
  | 'corporate-only';

export interface ToggleMeta {
  key: keyof ToggleState;
  label: string;
  tooltip: string;
  category: ToggleCategory;
}

/** 불리언 토글 목록 (라디오 제외) */
export const BOOL_TOGGLES: ToggleMeta[] = [
  // 공간
  {
    key: 'isBasement',
    label: '지하층 영업장',
    tooltip: '영업장이 지하층에 있으면 소방완비 면적 기준이 100㎡→66㎡로 강화됩니다.',
    category: 'space',
  },
  {
    key: 'isGroundFloorDirect',
    label: '1층+외부직결 출입구',
    tooltip: '주출입구가 건물 외부 지면과 직접 연결된 지상 1층(또는 지상 직접 접하는 층)이면 소방완비 면제 가능.',
    category: 'space',
  },
  {
    key: 'isOverAreaThreshold',
    label: '면적 100㎡(지하 66㎡) 이상',
    tooltip: '영업장 바닥면적이 100㎡(지하는 66㎡) 이상이면 소방완비증명서 필요 조건에 해당할 수 있습니다.',
    category: 'space',
  },
  {
    key: 'isNearSchool',
    label: '학교 50m 이내',
    tooltip: '학교환경위생정화구역(학교 50m 이내)은 심야영업 제한이 적용될 수 있습니다.',
    category: 'space',
  },
  // 부동산
  {
    key: 'isNewBuildOrRenovation',
    label: '신축·증축·용도변경 입점',
    tooltip: '기존 건물 임차+기존 정화조 그대로면 면제. 신축·증축·용도변경 시에는 정화조(오수처리시설) 신고가 필요합니다.',
    category: 'property',
  },
  // 운영
  {
    key: 'sellsAlcohol',
    label: '주류 판매',
    tooltip: '일반음식점에서만 주류 판매 가능. 영업 개시일로부터 30일 이내 세무서에 주류면허(의제) 신고 필요.',
    category: 'operations',
  },
  {
    key: 'usesLPG',
    label: 'LPG 사용 (도시가스 아닌 경우)',
    tooltip: 'LPG 용기를 사용하면 한국가스안전공사 완성검사 + 시군구청 신고가 필요합니다. 도시가스 사용 시 면제.',
    category: 'operations',
  },
  {
    key: 'hasLargeSignage',
    label: '간판 5㎡ 초과 또는 돌출간판',
    tooltip: '가로형 5㎡ 초과 또는 4층 이상 또는 돌출간판은 시군구청에 옥외광고물 신고가 필요합니다.',
    category: 'operations',
  },
  {
    key: 'sellsOnline',
    label: '배달앱·온라인 주문',
    tooltip: '배달앱(배민/쿠팡이츠/요기요) 입점 또는 자체 온라인 주문이 있으면 통신판매업 신고가 필요합니다.',
    category: 'operations',
  },
  {
    key: 'hasOwnOnlineOrder',
    label: '자사 온라인 주문 (배달앱 외 직접 결제)',
    tooltip: '자사 홈페이지·앱에서 직접 결제받는 경우 PG 계약 및 구매안전서비스 가입이 필요합니다. 배달앱 입점만이면 면제 가능.',
    category: 'operations',
  },
  {
    key: 'hasFlammableMaterials',
    label: '방염 대상물품 (목재·커튼·카펫 등)',
    tooltip: '인테리어에 목재·커튼·카펫 등 방염 대상물품이 있으면 소방완비증명(C) 전에 방염처리업체 계약·처리·성능시험이 선행되어야 합니다.',
    category: 'space',
  },
  // 인력
  {
    key: 'ownerHasLicense',
    label: '영업자가 조리사·영양사·위생사 면허 소지',
    tooltip: '해당 면허 소지자는 신규 위생교육(6시간) 면제. 식품위생법 §41.',
    category: 'staffing',
  },
  {
    key: 'ownerOnSite',
    label: '영업자 본인이 매장에 직접 참여',
    tooltip: '영업자 미상주 또는 2개소 이상 운영 시 종업원 1명을 위생관리책임자로 지정하여 대리 수강해야 합니다.',
    category: 'staffing',
  },
  {
    key: 'hiresForeignWorkers',
    label: '외국인 직원 (E-9·H-2 비자) 고용',
    tooltip: 'E-9(비전문취업)·H-2(방문취업 동포) 고용 시 고용노동부 고용센터 신고 필요. F-2/F-4/F-5/F-6은 불필요.',
    category: 'staffing',
  },
];

export const CATEGORY_LABELS: Record<ToggleCategory, string> = {
  'business-type': '사업자 형태',
  'food-service-type': '음식점 종별',
  space: '공간 조건',
  property: '부동산',
  operations: '운영',
  staffing: '인력',
  'corporate-only': '법인 직영 전용',
};

/** §3.6 과세 단위 라디오 옵션 (양자택일) */
export const CORPORATE_TAX_OPTIONS: { value: CorporateTaxMode; label: string; tooltip: string }[] = [
  {
    value: 'separate',
    label: '별도 사업자등록 (기본)',
    tooltip: '각 매장마다 독립 사업자번호 발급. 가장 일반적인 방식.',
  },
  {
    value: 'unit-tax',
    label: '사업자단위과세 (종된 사업장)',
    tooltip: '본점 사업자번호 유지, 종된 사업장 명세서 추가 제출. 부가세를 본점에서 한꺼번에 신고.',
  },
];
